/**
 * `/v1/recalls` — the publish body and the scope correction, in three stages.
 *
 * ## Why this resource is three schemas and not one, and why that is not a two-types
 * violation
 *
 * Every other resource in this API validates its whole body and then acts. This one
 * **interleaves shape and state by product decision**, and the decision is written into the
 * route it replaces: R6's check runs *"before scope validation and before any write"*,
 * because *"a brand whose second hazard is refused should be told about the recall already
 * running rather than about a lot code they mistyped in a request that was never going to
 * be accepted."*
 *
 * That is copy precedence, on the one path where a person is managing a safety incident.
 * A schema that validated the whole body at the transport boundary would answer
 * `VALIDATION_FAILED` where the shipped API answers `RECALL_ALREADY_ACTIVE`, on both
 * transports, silently — a wire move dressed as a refactor.
 *
 * So the stages are **authored once each** and composed two ways: the op runs them in the
 * route's order, around the state reads they are interleaved with, and
 * {@link publishRecallSchema} composes the same three for the SPA's form and for anyone
 * asking what the whole body is. One definition per field, two orders of consumption —
 * which is exactly the seam R7's ruling names, one layer lower than it was expecting it.
 *
 * The consequence for the tRPC mount is stated where it lands: the recall procedures take
 * the raw body, because an `.input(schema)` that ran first would put the precedence back on
 * one transport and not the other.
 */
import { z } from "zod";
import { RECALL_SCOPES } from "../recalls.js";
import { CHARSET_X, HTTP_URL, UUID } from "./patterns.js";
/**
 * Lots that may be named in one request.
 *
 * Not a product limit and not the ceiling that matters — the real one is `MAX_ENTRY_LINKS`,
 * checked in the op against the GTIN's actual link count. This exists so an absurd body is
 * refused before it is counted.
 */
export const MAX_LOTS_PER_REQUEST = 5000;
const PRODUCT_ID_MESSAGE = "productId must be a product's id.";
const SCOPE_MESSAGE = `scope must be one of ${RECALL_SCOPES.join(", ")}. Ranges are not expressible — ` +
    `the GS1 grain model has no range semantics, so a range is published as a ` +
    `whole-GTIN recall with the affected range stated in the notice text.`;
const LOTS_ARRAY_MESSAGE = "lots must be an array of AI 10 lot values.";
const LOTS_EMPTY_MESSAGE = "A lots-scoped recall must name at least one lot, or it matches nothing while " +
    "reporting itself active. Use scope: 'gtin' to recall every unit.";
const EXTERNAL_URL_MESSAGE = "externalUrl must be an http:// or https:// URL — your own or a " +
    "regulator's notice, used instead of the built-in page.";
/**
 * Stage 1 — **the only shape the route checks before it reads anything.**
 *
 * `UUID` rather than `z.uuid()`: the route tested `UUID.test(productId)`, which is the
 * shared pattern, and `z.uuid()` accepts a slightly different set. A migration is not the
 * place to discover which.
 */
export const recallTargetSchema = z
    .object({
    productId: z.preprocess((value) => (typeof value === "string" && value !== "" ? value : null), z.string(PRODUCT_ID_MESSAGE).regex(UUID, PRODUCT_ID_MESSAGE)),
})
    .describe("POST /v1/recalls — stage 1, the target, checked before R6.");
/**
 * Stage 2 — the scope, validated exactly as the merge will match it.
 *
 * **Nothing is trimmed, case-folded or de-duplicated except by exact equality.** §3.8: a
 * recall published against `Lot 42` when packs encode `LOT42` matches zero scans while the
 * dashboard reports it active — the worst silent failure in the product — and the
 * mitigation is instrumentation, never normalisation. A lot value that would not survive
 * `links_lot_check` is refused here instead, because a lot no scanner can encode is a scope
 * that can never match anything.
 *
 * The de-duplication that *is* applied is order-preserving and is not a normalisation: a
 * repeated lot in one request is a typo rather than an instruction to materialise the grain
 * twice, and `links_default_slot_key` would refuse the second pair anyway — with a 23505
 * that says nothing useful.
 */
export const recallScopeSchema = z
    .unknown()
    .superRefine((raw, ctx) => {
    const body = (typeof raw === "object" && raw !== null ? raw : {});
    const scope = body["scope"];
    if (typeof scope !== "string" ||
        !RECALL_SCOPES.includes(scope))
        return void ctx.addIssue({ code: "custom", message: SCOPE_MESSAGE });
    if (scope === "gtin")
        return;
    const raw_lots = body["lots"] ?? [];
    if (!Array.isArray(raw_lots) || raw_lots.some((lot) => typeof lot !== "string"))
        return void ctx.addIssue({ code: "custom", message: LOTS_ARRAY_MESSAGE });
    const lots = raw_lots;
    if (lots.length > MAX_LOTS_PER_REQUEST)
        return void ctx.addIssue({
            code: "custom",
            message: `lots names ${lots.length} values, which is more than one request carries.`,
        });
    const bad = lots.find((lot) => !CHARSET_X.test(lot));
    if (bad !== undefined)
        return void ctx.addIssue({
            code: "custom",
            message: `${JSON.stringify(bad)} is not a storable AI 10 lot value: 1–20 characters of GS1 ` +
                `charset X. Lot values are matched against a scanned value exactly and ` +
                `case-sensitively and are never normalised, so they are stored exactly as sent — ` +
                `check the value against what is encoded on the pack.`,
        });
    // `recalls_lots_match_scope` requires it, and the constraint is right: a lots-scoped
    // recall with no lots matches nothing while reporting itself active.
    if (new Set(lots).size === 0)
        ctx.addIssue({ code: "custom", message: LOTS_EMPTY_MESSAGE });
})
    .transform((raw) => {
    const body = (typeof raw === "object" && raw !== null ? raw : {});
    if (body["scope"] === "gtin")
        return { scope: "gtin", lots: [] };
    const lots = Array.isArray(body["lots"]) ? body["lots"] : [];
    return { scope: "lots", lots: [...new Set(lots)] };
})
    .describe("The recall scope — stage 2, checked after R6 and after the status read.");
/**
 * Stage 3 — the notice and its options.
 *
 * **The notice's shape is not validated beyond "it is an object", and that is a decision.**
 * §4 sketches it as lang-keyed structured fields shaped after the EU GPSR template — hazard,
 * affected-product identification, consumer action, contact/remedy — and sketches is all it
 * does; there is no ruled schema. Inventing one here would mean a **safety publish refused
 * for a shape reason**, which is the shape of invariant-3 violation the recall path is
 * arranged to avoid, and it would be a schema the dashboard then has to match rather than
 * one it derives. What is load-bearing is the other half of §4's sentence and it is honoured
 * absolutely: **nothing in this product composes or softens notice prose on a brand's
 * behalf**, because GPSR prohibits minimising wording. The brand's words are stored verbatim.
 * When the notice page ships (Phase 3) and the fields have consumers, the schema is a
 * ruling, not a route's guess.
 *
 * `overrideDefault` defaults to **on** and *"should almost never be turned off"*: with it
 * off, no override row is written and no suppression sweep runs, so the notice is invisible
 * to a phone camera. It is a control the user has, which is worth stating because a design
 * correction once described suppression as something they do not.
 */
export const recallNoticeSchema = z
    .object({
    externalUrl: z.preprocess((value) => (value === undefined || value === null ? null : value), z.string(EXTERNAL_URL_MESSAGE).regex(HTTP_URL, EXTERNAL_URL_MESSAGE).nullable()),
    overrideDefault: z.preprocess((value) => value !== false, z.boolean()),
    notice: z.preprocess((value) => (typeof value === "object" && value !== null ? value : {}), z.record(z.string(), z.unknown())),
    sourceNote: z.preprocess((value) => (typeof value === "string" ? value : null), z.string().nullable()),
    /**
     * The title the materialised default carries, or null for the composed fallback.
     *
     * The fallback lives in the op because it is composed **per grain** — a lot-scoped
     * notice names its lot — which is a fact about what is being written rather than about
     * what was sent.
     */
    noticeTitle: z.preprocess((value) => (typeof value === "string" && value.trim() !== "" ? value.trim() : null), z.string().nullable()),
})
    .describe("The notice and its options — stage 3.");
/**
 * The whole publish body, composed from the three stages.
 *
 * **For the SPA's form and for anyone asking what the body is — the server does not use
 * it**, because using it would validate the whole body before R6 and move the wire. That
 * asymmetry is the point of the file header, and it is written here too because this is the
 * export somebody will reach for when mounting a new transport.
 */
export const publishRecallSchema = recallTargetSchema
    .extend(recallNoticeSchema.shape)
    .extend({
    scope: z.enum(RECALL_SCOPES),
    lots: z.array(z.string()).optional(),
})
    .describe("POST /v1/recalls — the whole body, for form pre-validation.");
/**
 * `PATCH /v1/recalls/{id}/scope` — widen only.
 *
 * The body is {@link recallScopeSchema} plus an optional `noticeTitle` for the rows the
 * widening materialises. There is no separate schema for it: the widen route reads exactly
 * those two things, and a schema that named more would describe a request this API does not
 * accept.
 */
export const widenRecallSchema = z
    .object({
    scope: z.enum(RECALL_SCOPES),
    lots: z.array(z.string()).optional(),
    noticeTitle: z.string().optional(),
})
    .describe("PATCH /v1/recalls/{id}/scope — for form pre-validation. See the header.");
//# sourceMappingURL=recalls.js.map